import { CheckCircle2, Circle, PlayCircle, User, Undo2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDateBR } from "@/lib/api";

export const PhaseChecklist = ({
  phases,
  phasesState = [],
  onAdvance,
  onRegress,
  canAdvance = false,
  canRegress = false,
  variant = "internal",
}) => {
  const isPortal = variant === "portal";
  // Suporta phasesState em duas convenções (0-based e 1-based) — o backend
  // atualmente grava phase_index a partir do produto (0-based no advance,
  // 1-based no insert). Fazemos lookup tolerante por posição do array.
  const getState = (idx) => {
    // 1) posição direta no array
    if (phasesState[idx]) return phasesState[idx];
    // 2) match por phase_index 0-based
    const byZero = phasesState.find((p) => p.phase_index === idx);
    if (byZero) return byZero;
    // 3) match por phase_index 1-based
    const byOne = phasesState.find((p) => p.phase_index === idx + 1);
    if (byOne) return byOne;
    return { status: "pending" };
  };
  return (
    <div className="space-y-3">
      {phases.map((phase, idx) => {
        const state = getState(idx);
        const isDone = state.status === "completed";
        const isCurrent = state.status === "in_progress";
        const Icon = isDone ? CheckCircle2 : isCurrent ? PlayCircle : Circle;
        return (
          <div
            key={idx}
            className={cn(
              "flex items-start gap-3 p-3 md:p-4 border rounded-lg transition-colors",
              isCurrent
                ? "border-midnight bg-slate-50"
                : isDone
                ? "border-slate-200 bg-white"
                : "border-slate-200 bg-white",
            )}
            data-testid={`phase-row-${idx}`}
          >
            <Icon
              className={cn(
                "w-5 h-5 mt-0.5 flex-shrink-0",
                isDone
                  ? "text-emerald-600"
                  : isCurrent
                  ? "text-midnight"
                  : "text-slate-400",
              )}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-slate-500">
                    Fase {idx + 1}
                  </span>
                  <span className={cn(
                    "text-sm font-medium",
                    isDone ? "text-slate-700" : isCurrent ? "text-slate-900" : "text-slate-600",
                  )}>
                    {phase.title}
                  </span>
                </div>
                {isDone && state.completed_at && (
                  <span className="text-xs text-slate-500">
                    Concluída em {formatDateBR(state.completed_at)}
                  </span>
                )}
                {isCurrent && state.started_at && (
                  <span className="text-xs text-midnight font-medium">
                    Em andamento desde {formatDateBR(state.started_at)}
                  </span>
                )}
              </div>
              {phase.responsible_user_name && (
                <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1" data-testid={`phase-responsible-${idx}`}>
                  <User className="w-3 h-3" /> Responsável: <span className="font-medium text-slate-700">{phase.responsible_user_name}</span>
                </div>
              )}
              {!isPortal && canAdvance && !isDone && onAdvance && (
                <div className="mt-2 flex gap-2">
                  {!isCurrent && idx === phasesState.filter(p => p.status === "completed").length && (
                    <button
                      onClick={() => onAdvance(idx)}
                      data-testid={`phase-start-${idx}`}
                      className="text-xs font-medium px-2.5 py-1 rounded border border-midnight text-midnight hover:bg-midnight hover:text-white transition-colors"
                    >
                      Iniciar fase
                    </button>
                  )}
                  {isCurrent && (
                    <button
                      onClick={() => onAdvance(idx + 1)}
                      data-testid={`phase-complete-${idx}`}
                      className="text-xs font-medium px-2.5 py-1 rounded bg-midnight text-white hover:bg-ink transition-colors"
                    >
                      Concluir e avançar
                    </button>
                  )}
                </div>
              )}
              {/* Melhoria 5: botão 'Desfazer' para Diretora/Secretária.
                  Só aparece se a fase já está in_progress ou completed. */}
              {!isPortal && canRegress && onRegress && (isDone || isCurrent) && (
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => onRegress(idx)}
                    data-testid={`phase-regress-${idx}`}
                    className="text-xs font-medium px-2.5 py-1 rounded border border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white transition-colors inline-flex items-center gap-1"
                    title={isDone
                      ? "Desfazer conclusão desta fase (voltar para 'Em andamento')"
                      : "Desfazer início desta fase (voltar para 'Não iniciada')"}
                  >
                    <Undo2 className="w-3 h-3" />
                    Desfazer {isDone ? "conclusão" : "início"}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
